import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import {
  Nav,
  Footer,
  ClosingV5,
  Eyebrow,
  PillarAccent,
} from "../../App";
import { useContactPanel } from "../../components/ContactPanel";

/* ============================================================
   /showcase/veronique-gabai — Case Study
   Editorial monograph. Info-first, confident, restrained
   motion. Mirrors the brand's own pace — calm, premium,
   Mediterranean-luxury.
============================================================ */

/* Reusable atoms — keep this page narrow in scope */
const SectionLabel = ({ idx, label }) => (
  <div className="flex items-baseline gap-4 mb-7 md:mb-9">
    <span className="text-[0.65rem] md:text-[0.7rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] tabular-nums">
      {idx}
    </span>
    <span className="h-px flex-1 bg-white/[0.10] max-w-[80px]" />
    <span className="text-[0.65rem] md:text-[0.7rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
      {label}
    </span>
  </div>
);

/* ============================================================
   01 · Cinematic hero
============================================================ */
const HERO_IMG =
  "https://images.unsplash.com/photo-1541643600914-78b084683601?auto=format&fit=crop&w=2200&q=80";

const Hero = ({ onPrimary }) => (
  <>
    {/* 1. Full-bleed image — no text overlaying the photograph */}
    <section
      id="top"
      data-testid="vg-hero-image"
      className="relative overflow-hidden bg-[#0B0F14]"
    >
      <div className="relative w-full h-[78svh] min-h-[36rem] max-h-[52rem]">
        <img
          src={HERO_IMG}
          alt="Veronique Gabai — Mediterranean fragrance"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Subtle top fade for breadcrumb legibility only */}
        <span aria-hidden className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#0B0F14]/55 to-transparent" />

        {/* Breadcrumb only */}
        <div className="absolute inset-x-0 top-0">
          <div className="max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-20">
            <div className="flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-white/85">
              <Link to="/" className="hover:text-[#F4D64E] transition-colors">Sunday</Link>
              <span className="w-1 h-1 rounded-full bg-white/40" />
              <Link to="/showcase" className="hover:text-[#F4D64E] transition-colors">Showcase</Link>
              <span className="w-1 h-1 rounded-full bg-white/40" />
              <span className="text-[#F4D64E]">Veronique Gabai</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* 2. Title block — moved below the image, dark background */}
    <section
      data-testid="vg-hero"
      className="relative bg-[#0B0F14] border-b border-white/[0.06]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-14 items-end">
          <div className="lg:col-span-8">
            <span className="inline-flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E]">
              <span className="w-8 h-px bg-[#F4D64E]" />
              Case study · 01 / 11
            </span>
            <h1
              data-testid="vg-hero-title"
              className="font-display mt-7 text-white leading-[0.9] tracking-[-0.04em] font-black text-[3.5rem] sm:text-[5rem] md:text-[7rem] lg:text-[8.5rem] xl:text-[10rem]"
            >
              <span className="block">Veronique</span>
              <span className="block italic font-normal text-neutral-300">
                Gabai<span className="text-[#F4D64E]">.</span>
              </span>
            </h1>
            <p className="mt-9 max-w-[55ch] text-[1.1rem] md:text-[1.3rem] text-neutral-200 leading-[1.55]">
              A Mediterranean luxury fragrance, jewelry and aromatherapy
              house — built on a Shopify Plus storefront we&apos;ve shipped,
              scaled, and stewarded since launch.
            </p>
          </div>

          <div className="lg:col-span-4">
            {/* Project meta sidecar */}
            <dl className="grid grid-cols-2 gap-y-7 gap-x-6 text-[0.78rem] tracking-[0.18em] uppercase font-bold border-t border-white/[0.12] pt-8">
              <dt className="text-neutral-500">Industry</dt>
              <dd className="text-white">Luxury fragrance</dd>

              <dt className="text-neutral-500">Year started</dt>
              <dd className="text-white tabular-nums">2019</dd>

              <dt className="text-neutral-500">Status</dt>
              <dd className="text-[#F4D64E] flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F4D64E] animate-pulse" />
                Ongoing
              </dd>

              <dt className="text-neutral-500">Region</dt>
              <dd className="text-white">US · EU · French Riviera</dd>
            </dl>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={onPrimary}
                data-testid="vg-hero-cta"
                className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-6 py-3.5 rounded-md transition-colors"
              >
                Start a project
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
              <a
                href="https://veroniquegabai.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 text-[0.78rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] hover:text-white transition-colors"
              >
                Visit live site
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </>
);

/* ============================================================
   02 · At a glance — quick-read facts
============================================================ */
const AT_A_GLANCE_LEFT = `Veronique Gabai is a luxury house founded by namesake creator Veronique Gabai — a leader in the global fragrance industry whose career has spanned thirty years, from leading houses in Paris and New York to launching her own Mediterranean-inspired collection in 2018.

The brand spans Perfumery (the heart), modern Fine Jewelry (its Le Spritz perfume pendant is the gift of the season), and Aroma — a wellness-led aromatherapy line. Every product is built around naturals, sustainability, and the joy of the French Riviera.`;

const AT_A_GLANCE_RIGHT = [
  { k: "Touchpoints", v: "Storefront · CRM · Loyalty · Content" },
  { k: "Product lines", v: "Perfumery · Jewelry · Aroma · Beauty" },
  { k: "Press", v: "Vogue · Forbes · Coveteur" },
  { k: "Markets", v: "DTC · Selective wholesale" },
  { k: "Languages", v: "English · Français" },
];

const AtAGlance = () => (
  <section data-testid="vg-glance" className="relative bg-[#0B0F14] border-t border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <SectionLabel idx="01" label="At a glance" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-14 gap-y-12">
        <div className="lg:col-span-7">
          <h2 className="font-display text-[1.85rem] sm:text-[2.4rem] md:text-[3rem] lg:text-[3.5rem] font-extrabold tracking-[-0.025em] leading-[1.18] text-white">
            <span>
              A 30-year fragrance{" "}
              <span className="italic font-normal text-neutral-400">veteran&apos;s</span>{" "}
              <PillarAccent>own house</PillarAccent>, built for ecommerce.
            </span>
          </h2>
          <div className="mt-10 max-w-[58ch] text-[1.05rem] md:text-[1.15rem] text-neutral-300 leading-[1.7] space-y-6">
            {AT_A_GLANCE_LEFT.split("\n\n").map((p) => (
              <p key={p}>{p}</p>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-white/[0.08]">
          <dl className="grid grid-cols-1 gap-y-6">
            {AT_A_GLANCE_RIGHT.map((item) => (
              <div key={item.k} className="flex items-baseline justify-between gap-6 pb-5 border-b border-white/[0.06]">
                <dt className="text-[0.7rem] font-bold tracking-[0.3em] uppercase text-neutral-500 shrink-0">
                  {item.k}
                </dt>
                <dd className="text-[0.95rem] md:text-[1rem] text-white text-right">
                  {item.v}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================
   03 · The Brief
============================================================ */
const TheBrief = () => (
  <section data-testid="vg-brief" className="relative bg-[#0B0F14] border-t border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-14 gap-y-10">
        <div className="lg:col-span-4 lg:sticky lg:top-24 self-start">
          <SectionLabel idx="02" label="The brief" />
          <h2 className="font-display text-[2rem] md:text-[2.6rem] lg:text-[3.2rem] font-black tracking-[-0.03em] leading-[1.05] text-white">
            <span>
              Translate a{" "}
              <span className="italic font-normal text-neutral-400">perfumer&apos;s</span>{" "}
              <PillarAccent>nose</PillarAccent> into a digital storefront.
            </span>
          </h2>
        </div>

        <div className="lg:col-span-7 lg:col-start-6 space-y-8 text-[1.05rem] md:text-[1.15rem] text-neutral-300 leading-[1.7] max-w-[60ch]">
          <p>
            Fragrance is the hardest category to sell online. There is no
            scratch-and-sniff, no atomiser at the till. The brief was to
            replace that physical moment with a digital one — and do it
            without compromising the brand&apos;s editorial restraint.
          </p>
          <p>
            We were brought in to design and build the flagship Shopify
            Plus storefront, then continue as the day-to-day team behind
            it: feature work, content, paid media, and lifecycle.
          </p>
          <p>
            Three constants framed every decision. <span className="text-white">Editorial calm</span> over
            ecommerce noise. <span className="text-white">Olfactive truth</span> in how customers find what
            they want. And <span className="text-white">a Riviera point of view</span> — warm, generous,
            unmistakably French.
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================
   04 · What We Shipped — six chapters, six distinct layouts.
   Inspired by editorial monograph spreads (Pentagram, Manual,
   tat2-spirits) — each chapter has its own visual rhythm:
   • 01 Foundation — standard split (image L, text R)
   • 02 Discovery — full-bleed image break with text below
   • 03 Conversion — wide quote + two-image diptych below
   • 04 Sub-brand — three-image grid + text panel
   • 05 Hardware — VIDEO centerpiece + breakout caption
   • 06 Always-on — pull quote + single tall image
============================================================ */

const SHIPPED_VIDEO =
  "https://videos.pexels.com/video-files/4444076/4444076-uhd_2560_1440_25fps.mp4";

const ChapterEyebrow = ({ n, label }) => (
  <div className="flex items-baseline gap-4">
    <span className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] tabular-nums">
      {n} / 06
    </span>
    <span className="h-px flex-1 bg-white/[0.08] max-w-[60px]" />
    <span className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
      {label}
    </span>
  </div>
);

/* 01 — Foundation. Standard 7/5 split, image left. */
const Chapter01 = () => (
  <article data-testid="vg-shipped-01" className="relative bg-[#0B0F14] border-b border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-14 gap-y-10 items-center">
        <div className="lg:col-span-7 relative aspect-[16/10] overflow-hidden rounded-2xl border border-white/[0.06]">
          <img
            src="https://images.unsplash.com/photo-1592945403244-b3fbafd7f539?auto=format&fit=crop&w=1800&q=80"
            alt="Shopify Plus storefront"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/40 via-transparent to-transparent" />
        </div>
        <div className="lg:col-span-5">
          <ChapterEyebrow n="01" label="Foundation" />
          <h3 className="mt-6 font-display text-[1.7rem] md:text-[2.5rem] lg:text-[2.85rem] font-black tracking-[-0.03em] leading-[1.08] text-white">
            Shopify Plus storefront, custom-built.
          </h3>
          <p className="mt-7 text-[1rem] md:text-[1.075rem] text-neutral-400 leading-[1.7] max-w-[52ch]">
            A bespoke theme engineered for content density without losing
            breathing room. Multi-currency, multi-locale (EN / FR), B2B
            selective wholesale, and a checkout flow tuned around fragrance
            gifting — sets, samples, pendants.
          </p>
        </div>
      </div>
    </div>
  </article>
);

/* 02 — Discovery. Full-bleed image break, text below it. */
const Chapter02 = () => (
  <article data-testid="vg-shipped-02" className="relative bg-[#0B0F14] border-b border-white/[0.06]">
    {/* Full-bleed cinematic image */}
    <div className="relative h-[60svh] min-h-[24rem] max-h-[40rem] overflow-hidden">
      <img
        src="https://images.unsplash.com/photo-1541643600914-78b084683601?auto=format&fit=crop&w=2400&q=85"
        alt="Olfactive navigation"
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <span aria-hidden className="absolute inset-0 bg-gradient-to-b from-[#0B0F14]/30 via-transparent to-[#0B0F14]/85" />
      <span aria-hidden className="absolute inset-0 bg-gradient-to-r from-[#0B0F14]/60 via-transparent to-transparent" />
    </div>
    {/* Text breaks out below the image */}
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-14 gap-y-8">
        <div className="lg:col-span-5">
          <ChapterEyebrow n="02" label="Discovery" />
          <h3 className="mt-6 font-display text-[1.7rem] md:text-[2.5rem] lg:text-[3rem] font-black tracking-[-0.03em] leading-[1.05] text-white">
            Olfactive navigation —
            <span className="block italic font-normal text-neutral-300">three ways into a scent.</span>
          </h3>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 self-end">
          <p className="text-[1rem] md:text-[1.1rem] text-neutral-400 leading-[1.7] max-w-[58ch]">
            Most perfume sites force one taxonomy. We built three.
            <span className="text-white"> Olfactive Family</span> (Floral, Gourmand, Wood Musk Amber, Oud…).
            <span className="text-white"> Personality</span> (Radiant & Optimistic, Alluring & Mysterious…).
            <span className="text-white"> Discovery</span> (Sets, Layering, Online Consultation). The same
            product listed under each — the customer picks the door.
          </p>
        </div>
      </div>
    </div>
  </article>
);

/* 03 — Conversion. Wide quote with a two-image diptych beneath. */
const Chapter03 = () => (
  <article data-testid="vg-shipped-03" className="relative bg-[#0B0F14] border-b border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <ChapterEyebrow n="03" label="Conversion" />
      <h3 className="mt-7 font-display text-[2rem] sm:text-[2.6rem] md:text-[3.5rem] lg:text-[4rem] font-black tracking-[-0.035em] leading-[1.02] text-white max-w-5xl">
        Online Consultation —{" "}
        <span className="italic font-normal text-neutral-400">replacing</span>{" "}
        <PillarAccent>the counter</PillarAccent>.
      </h3>
      <p className="mt-9 text-[1.05rem] md:text-[1.15rem] text-neutral-300 leading-[1.7] max-w-[64ch]">
        A scheduled-call interface with a fragrance specialist embedded in the
        storefront. Customers fill a short olfactive questionnaire, book a
        slot, and walk away with a personalised list. Average order value
        follows.
      </p>

      {/* Two-image diptych */}
      <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8">
        <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-white/[0.06]">
          <img
            src="https://images.unsplash.com/photo-1585386959984-a4155224a1ad?auto=format&fit=crop&w=1200&q=80"
            alt="Online consultation"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute bottom-5 left-5 right-5 flex items-baseline justify-between text-[0.65rem] font-bold tracking-[0.3em] uppercase">
            <span className="text-white">Consultation flow</span>
            <span className="text-[#F4D64E]">A</span>
          </div>
        </div>
        <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-white/[0.06] md:translate-y-12">
          <img
            src="https://images.unsplash.com/photo-1592945403244-b3fbafd7f539?auto=format&fit=crop&w=1200&q=80"
            alt="Personalised list"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute bottom-5 left-5 right-5 flex items-baseline justify-between text-[0.65rem] font-bold tracking-[0.3em] uppercase">
            <span className="text-white">Recommendation set</span>
            <span className="text-[#F4D64E]">B</span>
          </div>
        </div>
      </div>
    </div>
  </article>
);

/* 04 — Sub-brand. Three-image vertical grid + text. */
const Chapter04 = () => (
  <article data-testid="vg-shipped-04" className="relative bg-[#0B0F14] border-b border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-14 gap-y-12 items-center">
        <div className="lg:col-span-5 lg:order-2">
          <ChapterEyebrow n="04" label="Sub-brand" />
          <h3 className="mt-6 font-display text-[1.7rem] md:text-[2.5rem] lg:text-[2.85rem] font-black tracking-[-0.03em] leading-[1.08] text-white">
            Aroma — a wellness arm,{" "}
            <span className="italic font-normal text-neutral-400">native to the same shelf.</span>
          </h3>
          <p className="mt-7 text-[1rem] md:text-[1.075rem] text-neutral-400 leading-[1.7] max-w-[52ch]">
            When the brand expanded into therapeutic aromatherapy, we shipped
            a connected sub-experience — Heart / Body / Soul benefits, a
            diagnostic test, separate discovery sets — without forcing
            customers to leave the perfumery world.
          </p>
        </div>

        {/* 3-image grid — varied aspect ratios */}
        <div className="lg:col-span-7 lg:order-1 grid grid-cols-2 grid-rows-2 gap-4 aspect-[5/4]">
          <div className="relative col-span-1 row-span-2 overflow-hidden rounded-2xl border border-white/[0.06]">
            <img
              src="https://images.unsplash.com/photo-1563170351-be82bc888aa4?auto=format&fit=crop&w=1200&q=80"
              alt="Heart"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <span className="absolute top-4 left-4 text-[0.6rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E]">Heart</span>
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-white/[0.06]">
            <img
              src="https://images.unsplash.com/photo-1487530811176-3780de880c2d?auto=format&fit=crop&w=1000&q=80"
              alt="Body"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <span className="absolute top-4 left-4 text-[0.6rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E]">Body</span>
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-white/[0.06]">
            <img
              src="https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?auto=format&fit=crop&w=1000&q=80"
              alt="Soul"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <span className="absolute top-4 left-4 text-[0.6rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E]">Soul</span>
          </div>
        </div>
      </div>
    </div>
  </article>
);

/* 05 — Hardware. VIDEO centerpiece. */
const Chapter05 = () => (
  <article data-testid="vg-shipped-05" className="relative bg-[#0B0F14] border-b border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-12">
      <div className="max-w-4xl">
        <ChapterEyebrow n="05" label="Hardware" />
        <h3 className="mt-7 font-display text-[2rem] sm:text-[2.6rem] md:text-[3.5rem] lg:text-[4rem] font-black tracking-[-0.035em] leading-[1.02] text-white">
          Le Spritz pendant —{" "}
          <span className="italic font-normal text-neutral-400">a digital launch</span>{" "}
          <PillarAccent>playbook</PillarAccent>.
        </h3>
      </div>
    </div>

    {/* Full-width video */}
    <div className="relative w-full aspect-[16/9] md:aspect-[21/9] overflow-hidden bg-[#0F141A]">
      <video
        src={SHIPPED_VIDEO}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster="https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?auto=format&fit=crop&w=2400&q=80"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/60 via-transparent to-transparent" />
      {/* Floating chip */}
      <div className="absolute top-6 left-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-black/50 backdrop-blur-md border border-white/10 text-[0.6rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E]">
        <span className="w-1.5 h-1.5 rounded-full bg-[#F4D64E] animate-pulse" />
        Launch reel · 2024
      </div>
    </div>

    {/* Breakout caption — three-column factsheet */}
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
        <div>
          <div className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] mb-3">Pre-launch</div>
          <p className="text-[0.95rem] md:text-[1rem] text-neutral-400 leading-[1.65] max-w-[36ch]">
            A waitlist + dedicated PDP with a 360° viewer, primed across
            email and CRM 6 weeks before drop.
          </p>
        </div>
        <div>
          <div className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] mb-3">Launch window</div>
          <p className="text-[0.95rem] md:text-[1rem] text-neutral-400 leading-[1.65] max-w-[36ch]">
            Coordinated CRM + paid push across Meta, Google, and TikTok —
            gift bundling on every PDP for the season.
          </p>
        </div>
        <div>
          <div className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] mb-3">Outcome</div>
          <p className="text-[0.95rem] md:text-[1rem] text-neutral-400 leading-[1.65] max-w-[36ch]">
            A quarter&apos;s revenue compressed into eight days — without
            cannibalising the core perfumery line.
          </p>
        </div>
      </div>
    </div>
  </article>
);

/* 06 — Always-on. Pull quote + single tall image. */
const Chapter06 = () => (
  <article data-testid="vg-shipped-06" className="relative bg-[#0B0F14] border-b border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-14 gap-y-12 items-stretch">
        {/* Left: quote-led */}
        <div className="lg:col-span-7 flex flex-col justify-between">
          <div>
            <ChapterEyebrow n="06" label="Always-on" />
            <blockquote className="mt-9 font-display text-[1.85rem] sm:text-[2.4rem] md:text-[3rem] lg:text-[3.5rem] font-bold tracking-[-0.025em] leading-[1.15] text-white">
              &ldquo;Five years of stewardship —
              <span className="italic font-normal text-neutral-400"> and counting</span>.&rdquo;
            </blockquote>
          </div>
          <div className="mt-12 lg:mt-0 pt-8 border-t border-white/[0.08] max-w-[52ch]">
            <p className="text-[1rem] md:text-[1.075rem] text-neutral-400 leading-[1.7]">
              Day-to-day ownership of feature work, A/B experiments, content
              production, lifecycle email & SMS, paid acquisition across Meta,
              Google, and TikTok, and the editorial calendar tied to seasonality
              and press moments.
            </p>
          </div>
        </div>

        {/* Right: single tall portrait image */}
        <div className="lg:col-span-5 relative aspect-[3/4] overflow-hidden rounded-2xl border border-white/[0.06]">
          <img
            src="https://images.unsplash.com/photo-1487530811176-3780de880c2d?auto=format&fit=crop&w=1200&q=80"
            alt="Always-on partnership"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/50 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 flex items-baseline justify-between text-[0.65rem] font-bold tracking-[0.3em] uppercase">
            <span className="text-white">Editorial calendar</span>
            <span className="text-[#F4D64E]">Y5</span>
          </div>
        </div>
      </div>
    </div>
  </article>
);

const WhatWeShipped = () => (
  <section data-testid="vg-shipped" className="relative bg-[#0B0F14] border-t border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-12">
      <div className="max-w-4xl">
        <SectionLabel idx="03" label="What we shipped" />
        <h2 className="font-display text-[2.5rem] md:text-[3.75rem] lg:text-[5rem] font-black tracking-[-0.04em] leading-[0.96] text-white">
          <span>
            Six chapters of work —{" "}
            <span className="italic font-normal text-neutral-400">across</span>{" "}
            <PillarAccent>five years</PillarAccent>.
          </span>
        </h2>
      </div>
    </div>
    <div className="mt-12">
      <Chapter01 />
      <Chapter02 />
      <Chapter03 />
      <Chapter04 />
      <Chapter05 />
      <Chapter06 />
    </div>
  </section>
);

/* ============================================================
   05 · By the numbers — restrained metrics row
============================================================ */
const NUMBERS = [
  { v: "5+", label: "Years in partnership" },
  { v: "2", label: "Connected sub-brands launched" },
  { v: "EN · FR", label: "Locales live" },
  { v: "8", label: "Days · Le Spritz launch window" },
];

const ByTheNumbers = () => (
  <section data-testid="vg-numbers" className="relative bg-[#0F141A] border-y border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <SectionLabel idx="04" label="By the numbers" />
      <ul className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-8 mt-2">
        {NUMBERS.map((n) => (
          <li key={n.label} className="flex flex-col gap-3">
            <span className="font-display text-[3.25rem] md:text-[4.25rem] lg:text-[5rem] font-black tracking-[-0.04em] leading-[0.92] text-white">
              {n.v.includes("·") ? (
                <span>
                  <span>{n.v.split(" · ")[0]}</span>
                  <span className="text-[#F4D64E] mx-1.5">·</span>
                  <span>{n.v.split(" · ")[1]}</span>
                </span>
              ) : (
                <span>{n.v}</span>
              )}
            </span>
            <span className="text-[0.7rem] md:text-[0.78rem] font-bold tracking-[0.22em] uppercase text-neutral-500 max-w-[24ch]">
              {n.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

/* ============================================================
   06 · In the press — quote tiles
============================================================ */
const PRESS = [
  {
    pub: "Vogue",
    quote:
      "Sur la Plage offers beach vibes in a bottle — immediately conjuring thoughts of sea, salt and sun.",
  },
  {
    pub: "Forbes",
    quote:
      "Each fragrance is meant to give you an overload of sensations and let you escape.",
  },
  {
    pub: "Coveteur",
    quote:
      "All you need to do is mist yourself with one of Gabai's scents and you'll find yourself transported — without ever needing to step on a plane.",
  },
];

const InThePress = () => (
  <section data-testid="vg-press" className="relative bg-[#0B0F14] border-t border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <SectionLabel idx="05" label="In the press" />
      <h2 className="font-display text-[2.25rem] md:text-[3rem] lg:text-[3.75rem] font-black tracking-[-0.035em] leading-[1.05] text-white max-w-3xl">
        <span>
          The work showed up{" "}
          <span className="italic font-normal text-neutral-400">where it</span>{" "}
          <PillarAccent>matters</PillarAccent>.
        </span>
      </h2>

      <ul className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
        {PRESS.map((p, i) => (
          <li
            key={p.pub}
            data-testid={`vg-press-${i + 1}`}
            className="relative bg-[#0F141A] border border-white/[0.06] rounded-2xl p-7 md:p-9 hover:border-[#F4D64E]/30 transition-colors"
          >
            <span className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] tabular-nums">
              {String(i + 1).padStart(2, "0")}
            </span>
            <blockquote className="mt-7 font-display text-[1.05rem] md:text-[1.15rem] lg:text-[1.2rem] font-bold tracking-[-0.005em] text-white leading-[1.45]">
              &ldquo;{p.quote}&rdquo;
            </blockquote>
            <div className="mt-7 pt-5 border-t border-white/[0.08] flex items-center justify-between">
              <span className="font-display text-[1.4rem] md:text-[1.6rem] font-black tracking-[-0.015em] uppercase text-white">
                {p.pub}
              </span>
              <span className="text-[0.65rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
                Editorial coverage
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

/* ============================================================
   07 · Outcome + Ongoing — the conclusion
============================================================ */
const Outcome = () => (
  <section
    data-testid="vg-outcome"
    className="relative bg-[#0B0F14] border-t border-white/[0.06] overflow-hidden"
  >
    <span
      aria-hidden
      className="absolute -top-32 -right-24 w-[40rem] h-[40rem] rounded-full bg-[#F4D64E]/[0.08] blur-3xl"
    />
    <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-28 md:py-40">
      <SectionLabel idx="06" label="Where it stands" />
      <div className="max-w-5xl">
        <p className="font-display text-[2rem] sm:text-[2.75rem] md:text-[3.5rem] lg:text-[4.5rem] font-black tracking-[-0.035em] leading-[1.05] text-white">
          <span>
            Five years in,{" "}
            <span className="italic font-normal text-neutral-400">we&apos;re</span>{" "}
            <PillarAccent>still building</PillarAccent> — together.
          </span>
        </p>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 max-w-4xl">
          <p className="text-[1.05rem] md:text-[1.15rem] text-neutral-300 leading-[1.7]">
            What started as a Shopify Plus build is now a continuous
            engagement — design, engineering, content, lifecycle, and
            paid media all under one roof. Every quarter brings a new
            launch, a new collaboration, a new market.
          </p>
          <p className="text-[1.05rem] md:text-[1.15rem] text-neutral-300 leading-[1.7]">
            That&apos;s the work we want to do. The kind that compounds
            year over year — where success isn&apos;t a single moment, but
            a long, deliberate climb. Veronique Gabai is the
            partnership we measure others against.
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================
   08 · Next project tease + closing
============================================================ */
const NextProject = ({ onContact }) => (
  <section
    data-testid="vg-next"
    className="relative bg-[#0F141A] border-t border-white/[0.06]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-10 items-center">
        <div className="lg:col-span-7">
          <span className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
            Next case study · 02 / 11
          </span>
          <Link
            to="/showcase"
            data-testid="vg-next-link"
            className="group block mt-6"
          >
            <div className="font-display text-[2.5rem] sm:text-[3.5rem] md:text-[4.5rem] lg:text-[5.5rem] font-black tracking-[-0.04em] leading-[0.96] text-white group-hover:text-[#F4D64E] transition-colors">
              Ming Yu Wang
            </div>
            <div className="mt-4 inline-flex items-center gap-3 text-[0.78rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] group-hover:text-white transition-colors">
              Designer Jewelry · 2020
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </div>
          </Link>
        </div>

        <div className="lg:col-span-5 lg:text-right">
          <p className="text-[0.95rem] md:text-[1rem] text-neutral-400 leading-[1.65] max-w-[40ch] lg:ml-auto">
            Ready to start your own chapter?
          </p>
          <button
            type="button"
            onClick={onContact}
            data-testid="vg-next-cta"
            className="group mt-6 inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-7 py-4 rounded-md transition-colors"
          >
            Let&apos;s talk
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================
   PAGE
============================================================ */
export default function VeroniqueGabaiPage() {
  const { open: openContact } = useContactPanel();

  useEffect(() => {
    document.title = "Veronique Gabai — Sunday Digital Showcase";
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div
      data-testid="vg-page"
      data-variant="v5"
      className="min-h-screen bg-[#0B0F14] text-neutral-100 font-body"
    >
      <Nav />
      <main>
        <Hero onPrimary={openContact} />
        <AtAGlance />
        <TheBrief />
        <WhatWeShipped />
        <ByTheNumbers />
        <InThePress />
        <Outcome />
        <NextProject onContact={openContact} />
        <ClosingV5 />
      </main>
      <Footer />
    </div>
  );
}
